// ─── src/components/dashboard/DoorCodesPanel.tsx ─────────────────────────────
//
// Door codes for one event: create, share (copy / WhatsApp) and revoke.
// Works for both the admin and catalyst APIs — the caller passes the calls.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, KeyRound, Loader2, MessageCircle, Plus, X } from "lucide-react";
import { doorCodeError, doorCodeMessage, type DoorCode } from "../../api/doorCodes";

export function formatWhen(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 1800);
    } catch {
      window.prompt("Copy:", text);
    }
  };
  return { copied, copy };
}

/** Copy / WhatsApp / Revoke buttons for one code. */
export function DoorCodeActions({
  code,
  copied,
  onCopy,
  onRevoke,
  revoking,
}: {
  code: DoorCode;
  copied: boolean;
  onCopy: () => void;
  onRevoke: () => void;
  revoking: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button type="button" onClick={onCopy} className="btn-ghost text-xs !px-2.5 !py-1.5 inline-flex items-center gap-1">
        {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy message"}
      </button>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(doorCodeMessage(code))}`}
        target="_blank"
        rel="noreferrer"
        className="btn-ghost text-xs !px-2.5 !py-1.5 inline-flex items-center gap-1"
      >
        <MessageCircle size={13} /> WhatsApp
      </a>
      <button
        type="button"
        disabled={revoking}
        onClick={() => {
          if (window.confirm(`Revoke ${code.code}? Anyone scanning with it stops immediately.`)) onRevoke();
        }}
        className="text-xs px-2.5 py-1.5 rounded-lg text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1 disabled:opacity-50"
      >
        <X size={13} /> Revoke
      </button>
    </div>
  );
}

export function DoorCodesPanel({
  queryKey,
  list,
  create,
  revoke,
}: {
  queryKey: unknown[];
  list: () => Promise<DoorCode[]>;
  create: (label?: string) => Promise<DoorCode>;
  revoke: (id: string) => Promise<DoorCode>;
}) {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const { copied, copy } = useCopy();

  const { data: codes, isLoading } = useQuery({ queryKey, queryFn: list });
  const add = useMutation({
    mutationFn: () => create(label.trim() || undefined),
    onSuccess: () => {
      setLabel("");
      qc.invalidateQueries({ queryKey });
    },
  });
  const remove = useMutation({
    mutationFn: revoke,
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const active = codes?.filter((c) => c.active) ?? [];
  const inactive = codes?.filter((c) => !c.active) ?? [];

  return (
    <div className="card p-4 space-y-4">
      <div>
        <p className="text-white text-sm font-semibold flex items-center gap-2">
          <KeyRound size={15} className="text-brand-green" /> Door codes for receptionists
        </p>
        <p className="text-brand-muted text-xs mt-1 leading-5">
          Send a code to each door team. They open the link on their own phone, type their name and
          scan at <span className="font-mono">scan.startupsyria.org</span> — no dashboard login. A code
          only admits this event's tickets and expires at the end of the event day.
        </p>
      </div>

      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={120}
          placeholder="Optional note, e.g. Main entrance"
          className="flex-1 min-w-[200px] bg-brand-navy border border-brand-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-brand-muted focus:outline-none focus:border-brand-green"
        />
        <button type="submit" disabled={add.isPending} className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
          {add.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} New code
        </button>
      </form>
      {add.isError && <p className="text-xs text-red-400">{doorCodeError(add.error)}</p>}

      {isLoading ? (
        <p className="text-xs text-brand-muted">Loading…</p>
      ) : active.length === 0 ? (
        <p className="text-xs text-brand-muted">No active codes yet.</p>
      ) : (
        <ul className="divide-y divide-brand-border rounded-xl border border-brand-border">
          {active.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-3 p-3">
              <span className="font-mono text-lg font-bold tracking-[0.15em] text-white">{c.code}</span>
              <div className="min-w-0 flex-1 text-xs text-brand-muted">
                {c.label && <p className="truncate text-white/80">{c.label}</p>}
                <p>
                  Expires {formatWhen(c.expiresAt)}
                  {c.lastUsedAt && <> · last used {formatWhen(c.lastUsedAt)}</>}
                </p>
              </div>
              <DoorCodeActions
                code={c}
                copied={copied === c.id}
                onCopy={() => copy(c.id, doorCodeMessage(c))}
                onRevoke={() => remove.mutate(c.id)}
                revoking={remove.isPending}
              />
            </li>
          ))}
        </ul>
      )}

      {inactive.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs text-brand-muted">Expired or revoked ({inactive.length})</summary>
          <ul className="mt-2 space-y-1 text-xs text-brand-muted">
            {inactive.map((c) => (
              <li key={c.id}>
                <span className="font-mono line-through">{c.code}</span>
                {c.label && <> · {c.label}</>} · {c.revokedAt ? "revoked" : "expired"}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
